import React, { useState, memo, useEffect, useRef, useMemo } from 'react';
import { InputGroup, Textarea, InputRightElement, IconButton, Flex, useToast, Menu, MenuButton, MenuList, MenuItem, Button, Text, Box, VStack, useColorModeValue, Tooltip, Spinner } from "@chakra-ui/react";
import { FaPaperPlane, FaStop, FaBolt, FaBrain, FaPaperclip, FaTimes, FaRegFile } from "react-icons/fa";
import ResizeTextarea from "react-textarea-autosize";
import fetchStopMessage from '../helpers/fetchStopMessage';
import { MessageInterface } from '../configs';
import fetchAutocompleteQuestion from '../helpers/fetchAutocompleteQuestion';
import debounce from 'lodash/debounce';

interface DialogueProps {
    useAutocomplete: boolean;
    /** The likely next message, shown in the empty box; Tab (or the hint) puts it in the box. */
    nextMessageSuggestion?: string;
    dialogueBox: string;
    handleDialogueBoxChange: (value: string) => void;
    sendMessage: (overrideText?: string) => void;
    waitAnswer: boolean;
    // Run status of the open chat, already corrected for runs the server lost.
    chatStatus?: string;
    currentChat: string;
    isDesktop: boolean | undefined;
    apiAddress: string;
    chatMessages: MessageInterface[];
    // The backend's estimate for the whole chat, given while chatMessages holds only its tail.
    serverContextTokens?: number;
    responseMode?: string;
    handleResponseModeChange?: (mode: string) => void;
    dialogueFiles: { file: File, name: string, id?: string, uploading: boolean }[];
    onFileSelect: (files: File[]) => void;
    onFileRemove: (index: number) => void;
    acceptedFormats: string[];
    enableFileUpload?: boolean;
}

const MAX_FILES = 10;

const DialogueInput = memo(function DialogueInput({
    useAutocomplete,
    nextMessageSuggestion = '',
    dialogueBox,
    handleDialogueBoxChange,
    sendMessage,
    waitAnswer,
    chatStatus,
    currentChat,
    isDesktop,
    apiAddress,
    chatMessages,
    serverContextTokens,
    responseMode,
    handleResponseModeChange,
    dialogueFiles,
    onFileSelect,
    onFileRemove,
    acceptedFormats,
    enableFileUpload = true,
}: DialogueProps) {
    const toast = useToast();
    const [suggestion, setSuggestion] = useState('');
    const [textareaHeight, setTextareaHeight] = useState<number>(0);
    const [suggestionHeight, setSuggestionHeight] = useState<number>(0);
    const [localResponseMode, setLocalResponseMode] = useState(responseMode || 'fast');
    const lastInputRef = useRef('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
        const files = Array.from(e.target.files || []);
        const validFiles: File[] = [];
        const invalidFiles: string[] = [];

        for (const file of files) {
            const ext = '.' + file.name.split('.').pop()?.toLowerCase();
            if (!acceptedFormats.includes(ext)) {
                invalidFiles.push(file.name);
            } else {
                validFiles.push(file);
            }
        }

        if (invalidFiles.length > 0) {
            toast({
                position: 'top',
                title: 'Invalid file format',
                description: `Unsupported: ${invalidFiles.join(', ')}. Accepted: ${acceptedFormats.join(', ')}`,
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        }

        if (validFiles.length > 0) {
            onFileSelect(validFiles);
        }

        // Reset input so the same file can be re-selected
        if (fileInputRef.current) fileInputRef.current.value = '';
    }

    function removeFile(index: number) {
        onFileRemove(index);
    }

    useEffect(() => {
        if (responseMode) {
            setLocalResponseMode(responseMode);
        }
    }, [responseMode]);

    useEffect(() => {
        setSuggestion('');
    }, [currentChat]);

    const fetchSuggestion = useMemo(
        () => debounce(async (value: string) => {
            if (value.trim()) {
                try {
                    const completion = await fetchAutocompleteQuestion(currentChat, value, apiAddress);
                    if (completion && completion.includes(value) && value === lastInputRef.current) {
                        setSuggestion(completion);
                    }
                } catch (error) {
                    console.error('Autocomplete error:', error);
                }
            } else {
                setSuggestion('');
            }
        }, 500),
        [currentChat, apiAddress]
    );

    useEffect(() => {
        return () => {
            fetchSuggestion.cancel();
        };
    }, [fetchSuggestion]);

    function handleLocalChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
        const value = e.target.value;
        lastInputRef.current = value;
        handleDialogueBoxChange(value);
        if (!suggestion.startsWith(value) || !value) {
            setSuggestion('');
        }
        if (value.trim()) {
            if (useAutocomplete) {
                fetchSuggestion(value);
            }
        }
    }

    const showNextMessage = !!nextMessageSuggestion && !dialogueBox;
    function acceptNextMessage() {
        handleDialogueBoxChange(nextMessageSuggestion);
        setSuggestion('');
        textareaRef.current?.focus();
    }

    function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
        if (e.key === 'Tab' && !e.shiftKey && showNextMessage) {
            e.preventDefault();
            acceptNextMessage();
        } else if (e.key === 'Tab' && suggestion) {
            e.preventDefault();
            handleDialogueBoxChange(suggestion);
            setSuggestion('');
        } else if (e.key === 'Enter' && !e.shiftKey && !e.altKey && !e.ctrlKey) {
            if (currentChat) {
                if ((!chatStatus) || (['completed', 'canceled'].includes(chatStatus))) {
                    e.preventDefault();
                    sendMessage();
                }
            } else {
                e.preventDefault();
                sendMessage();
            }

        }
    }

    function handleStopMessage() {
        fetchStopMessage(currentChat, apiAddress);

        // Restore the last user message to the text box so they can edit it
        let textToRestore = lastInputRef.current;

        // Fallback to searching chatMessages if local ref is empty
        if (!textToRestore && chatMessages && Array.isArray(chatMessages)) {
            const lastUserMessage = [...chatMessages].reverse().find(msg => msg.role === 'user');
            if (lastUserMessage) {
                if (lastUserMessage.content && Array.isArray(lastUserMessage.content) && lastUserMessage.content[0]?.text) {
                    textToRestore = lastUserMessage.content[0].text;
                } else if (typeof lastUserMessage.content === 'string') {
                    textToRestore = lastUserMessage.content as unknown as string;
                }
            }
        }

        if (textToRestore) {
            handleDialogueBoxChange(textToRestore);
        }

        toast({
            position: 'top',
            title: "Cancelling message...",
            status: "info",
            duration: 3000
        });
    }

    function handleResize(height: number) {
        setTextareaHeight(height);
    }

    function handleSuggestionResize(height: number) {
        setSuggestionHeight(height);
    }

    // Use the larger of input height or suggestion height
    const effectiveMinHeight = suggestion && suggestionHeight > textareaHeight
        ? `${suggestionHeight}px`
        : textareaHeight > 0 ? `${textareaHeight}px` : 'auto';

    function handleModeChange(mode: string) {
        setLocalResponseMode(mode);
        if (handleResponseModeChange) {
            handleResponseModeChange(mode);
        }
    }

    const getModeConfig = (mode: string) => {
        switch (mode) {
            case 'fast':
                return { icon: FaBolt, label: 'Fast Responses', description: 'Quick response', color: '#163c49' };
            case 'expert':
                return { icon: FaBrain, label: 'Expert Reasoning', description: 'Deeper analysis', color: '#163c49' };
            default:
                return { icon: FaBolt, label: 'Fast Responses', description: 'Quick response', color: '#163c49' };
        }
    };

    const currentModeConfig = getModeConfig(localResponseMode);

    // Theme colors
    const textColor = useColorModeValue("blackAlpha.800", "whiteAlpha.900");
    const borderColor = useColorModeValue("#b4b4b4", "gray.600");
    const menuButtonBg = useColorModeValue("white", "gray.700");
    const menuButtonBorder = useColorModeValue("#d0d0d0", "gray.600");
    const menuButtonHoverBg = useColorModeValue('#fafafa', 'gray.600');
    const menuButtonHoverBorder = useColorModeValue('#9a9a9a', 'gray.500');
    const menuButtonActiveBg = useColorModeValue('#f5f5f5', 'gray.800');
    const menuButtonActiveBorder = useColorModeValue('#163c49', '#4FD1C5');
    const menuButtonColor = useColorModeValue("#163c49", "#4FD1C5");
    const menuListBg = useColorModeValue("white", "gray.700");
    const menuItemBgActive = useColorModeValue('#e8f4f8', 'rgba(79, 209, 197, 0.1)');
    const menuItemHoverBg = useColorModeValue('#d4e9f0', 'gray.600');
    const menuItemHoverBgAlt = useColorModeValue('#f5f5f5', 'gray.600');
    const menuItemActiveBgAlt = useColorModeValue('#f0f0f0', 'gray.600');
    const menuItemText = useColorModeValue("#163c49", "#4FD1C5");
    const menuItemDesc = useColorModeValue("gray.600", "gray.400");
    const stopIconColor = useColorModeValue("#b4b3b3", "gray.500");
    const sendIconColor = useColorModeValue("#163c49", "#4FD1C5");
    const sendIconColorDisabled = useColorModeValue("#b4b3b3", "gray.500");
    const sendButtonBg = useColorModeValue("#163c49", "#014641");
    const sendButtonBgDisabled = useColorModeValue("gray", "gray.600");
    const primaryColor = useColorModeValue("#163c49", "#4FD1C5");

    // Context window token estimation — keep TOKEN_LIMIT in sync with
    // MAX_TOKENS_PER_DIALOGUE in api/rate_limiter.py
    const TOKEN_LIMIT = 800_000;
    const contextUsage = useMemo(() => {
        if (!chatMessages || chatMessages.length === 0) {
            return { estimatedTokens: 0, percentage: 0 };
        }
        const charsCount = JSON.stringify(chatMessages).length;
        const localTokens = Math.round(charsCount / 4) + 2000; // +2000 for system prompt
        const estimatedTokens = Math.max(localTokens, serverContextTokens ?? 0);
        const percentage = Math.min((estimatedTokens / TOKEN_LIMIT) * 100, 100);
        return { estimatedTokens, percentage };
    }, [chatMessages, serverContextTokens]);

    // Context warning level: null below 80%, 'warning' at 80–90%, 'critical' at 90%+
    const contextWarningLevel = contextUsage.percentage >= 90
        ? 'critical'
        : contextUsage.percentage >= 80
            ? 'warning'
            : null;

    const getRingColor = (pct: number) => {
        if (pct >= 90) return '#E53E3E'; // red
        if (pct >= 70) return '#DD6B20'; // orange
        if (pct >= 50) return '#D69E2E'; // yellow
        return '#38A169'; // green
    };

    const isOverLimit = contextUsage.percentage >= 95;
    const ringTrackColor = useColorModeValue('rgba(0,0,0,0.15)', 'rgba(255,255,255,0.15)');
    const warnBannerBg = useColorModeValue('rgba(214, 158, 46, 0.12)', 'rgba(214, 158, 46, 0.22)');
    const warnBannerColor = useColorModeValue('#975A16', '#F6E05E');
    const critBannerBg = useColorModeValue('rgba(229, 62, 62, 0.1)', 'rgba(229, 62, 62, 0.22)');
    const critBannerColor = useColorModeValue('#C53030', '#FEB2B2');
    const textareaBorderColor = contextWarningLevel === 'critical'
        ? '#E53E3E'
        : contextWarningLevel === 'warning'
            ? '#D69E2E'
            : borderColor;

    const SUMMARIZE_PROMPT = "Please write a summary of this conversation that I can paste into a new chat to continue where we left off. Include the key questions asked, the conclusions reached, and any important context or references that are still relevant.";

    function handleSummarizeClick() {
        if (waitAnswer) return;
        if (chatStatus && !['completed', 'canceled'].includes(chatStatus)) return;
        sendMessage(SUMMARIZE_PROMPT);
    }
    const chipBg = useColorModeValue('#f0f0ef', 'gray.600');
    const chipColor = useColorModeValue('gray.700', 'gray.200');
    const chipBorder = useColorModeValue('gray.300', 'gray.500');
    const paperclipColor = useColorModeValue('gray.500', 'gray.400');
    const paperclipHoverBg = useColorModeValue('gray.100', 'gray.600');
    const controlButtonSize = isDesktop ? "2.25rem" : "2rem";
    const nextHintColor = useColorModeValue("gray.500", "gray.400");
    const nextHintBorder = useColorModeValue("gray.300", "gray.600");
    const nextHintHoverBg = useColorModeValue("gray.50", "whiteAlpha.100");
    const leftControlPadding = enableFileUpload ? (isDesktop ? "5.5rem" : "5rem") : (isDesktop ? "3.5rem" : "3rem");
    const paperclipLeft = isDesktop ? "3.5rem" : "3rem";
    const inputPaddingY = 5;
    const inputPaddingRight = isDesktop ? "5.25rem" : "4.75rem";
    // Room for the "Tab" hint beside the send button while a suggested next message fills the box.
    const nextHintPaddingRight = isDesktop ? "8.75rem" : "8rem";

    return (
        <Box w="100%" position="relative">
            {/* Context limit warning banner */}
            {contextWarningLevel && !isOverLimit && (
                <Flex
                    align="center"
                    justify="center"
                    mb={2}
                    px={4}
                    py={2}
                    borderRadius="10px"
                    bg={contextWarningLevel === 'critical' ? critBannerBg : warnBannerBg}
                >
                    {contextWarningLevel === 'critical' ? (
                        <Text fontSize={isDesktop ? "sm" : "xs"} color={critBannerColor} textAlign="center">
                            {Math.round(contextUsage.percentage)}% of context used.{' '}
                            <Text
                                as="span"
                                role="button"
                                fontWeight="700"
                                textDecoration="underline"
                                cursor="pointer"
                                onClick={handleSummarizeClick}
                            >
                                Summarize this chat
                            </Text>
                            , then start a new chat to continue.
                        </Text>
                    ) : (
                        <Text fontSize={isDesktop ? "sm" : "xs"} color={warnBannerColor} textAlign="center">
                            This chat is reaching its limit ({Math.round(contextUsage.percentage)}% of context used). Consider starting a new chat soon.
                        </Text>
                    )}
                </Flex>
            )}
            {/* Context full banner — normal sending disabled, summarize still allowed */}
            {isOverLimit && (
                <Flex
                    align="center"
                    justify="center"
                    mb={2}
                    px={4}
                    py={2}
                    borderRadius="10px"
                    bg={critBannerBg}
                >
                    <Text fontSize={isDesktop ? "sm" : "xs"} color={critBannerColor} textAlign="center">
                        This chat is full.{' '}
                        <Text
                            as="span"
                            role="button"
                            fontWeight="700"
                            textDecoration="underline"
                            cursor="pointer"
                            onClick={handleSummarizeClick}
                        >
                            Summarize this chat
                        </Text>
                        , then start a new chat to continue.
                    </Text>
                </Flex>
            )}
            {/* Hidden file input */}
            {enableFileUpload && (
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept={acceptedFormats.join(',')}
                    multiple
                    style={{ display: 'none' }}
                />
            )}
            {/* File chips */}
            {enableFileUpload && dialogueFiles.length > 0 && (
                <Flex gap={2} mb={2} flexWrap="wrap" px={1}>
                    {dialogueFiles.map((file, i) => (
                        <Flex
                            key={`${file.name}-${i}`}
                            align="center"
                            gap={1.5}
                            bg={chipBg}
                            border="1px solid"
                            borderColor={chipBorder}
                            borderRadius="full"
                            px={3}
                            py={1}
                            fontSize="12px"
                            fontWeight={500}
                            color={chipColor}
                        >
                            {file.uploading ? (
                                <Spinner size="xs" speed="0.6s" color={chipColor} />
                            ) : (
                                <Box as={FaRegFile} fontSize="10px" opacity={0.7} />
                            )}
                            <Text maxW="150px" isTruncated>{file.name}</Text>
                            <IconButton
                                aria-label="Remove file"
                                icon={<FaTimes />}
                                size="xs"
                                variant="ghost"
                                minW="16px"
                                h="16px"
                                fontSize="8px"
                                borderRadius="full"
                                color={chipColor}
                                _hover={{ bg: 'red.100', color: 'red.500' }}
                                onClick={() => removeFile(i)}
                            />
                        </Flex>
                    ))}
                </Flex>
            )}
            <InputGroup size='md' position="relative">
                <Textarea
                    ref={textareaRef}
                    isDisabled={waitAnswer}
                    placeholder={showNextMessage ? nextMessageSuggestion : "Type your question for ChatSOC here..."}
                    aria-description={showNextMessage ? "Suggested next message: press Tab to use it" : undefined}
                    py={inputPaddingY}
                    size="md"
                    paddingLeft={leftControlPadding}
                    paddingRight={showNextMessage ? nextHintPaddingRight : inputPaddingRight}
                    borderRadius="15px"
                    rows={1}
                    boxShadow={contextWarningLevel ? `0 0 0 1px ${textareaBorderColor}` : "md"}
                    border="1px solid"
                    borderColor={textareaBorderColor}
                    value={dialogueBox}
                    onChange={handleLocalChange}
                    onKeyDown={handleKeyDown}
                    color={textColor}
                    as={ResizeTextarea}
                    maxRows={4}
                    resize="none"
                    id="dialogue-box"
                    onHeightChange={handleResize}
                    transition="padding-left 0.2s ease, min-height 0.15s ease"
                    minHeight={effectiveMinHeight}
                />
                {suggestion && dialogueBox && (
                    <Textarea
                        position="absolute"
                        inset={0}
                        w="100%"
                        py={inputPaddingY}
                        size="md"
                        pointerEvents="none"
                        paddingLeft={leftControlPadding}
                        paddingRight={inputPaddingRight}
                        borderRadius="15px"
                        rows={1}
                        boxShadow="md"
                        border="1px solid"
                        borderColor={textareaBorderColor}
                        placeholder={suggestion}
                        color={textColor}
                        as={ResizeTextarea}
                        maxRows={4}
                        resize="none"
                        onHeightChange={handleSuggestionResize}
                        minHeight={effectiveMinHeight}
                        transition="padding-left 0.2s ease, min-height 0.15s ease"
                    />
                )}
                <Box
                    position="absolute"
                    left="0.75rem"
                    top="50%"
                    transform="translateY(-50%)"
                    zIndex={2}
                >
                    <Menu>
                        <Tooltip label={currentModeConfig.label} placement="top" hasArrow borderRadius="8px" fontSize="xs">
                            <MenuButton
                                as={Button}
                                aria-label={`Response mode: ${currentModeConfig.label}`}
                                size={isDesktop ? "sm" : "xs"}
                                height={controlButtonSize}
                                minWidth={controlButtonSize}
                                width={controlButtonSize}
                                borderRadius="full"
                                border="1px solid"
                                borderColor={menuButtonBorder}
                                bg={menuButtonBg}
                                _hover={{ bg: menuButtonHoverBg, borderColor: menuButtonHoverBorder }}
                                _active={{ bg: menuButtonActiveBg, borderColor: menuButtonActiveBorder }}
                                p={0}
                                boxShadow="sm"
                                color={menuButtonColor}
                                transition="background-color 0.2s ease, border-color 0.2s ease"
                            >
                                <Box as={currentModeConfig.icon} color={primaryColor} fontSize={isDesktop ? "sm" : "xs"} mx="auto" />
                            </MenuButton>
                        </Tooltip>
                        <MenuList
                            minW={isDesktop ? "220px" : "190px"}
                            borderRadius="15px"
                            border="1px solid"
                            borderColor={borderColor}
                            boxShadow="lg"
                            p={2}
                            bg={menuListBg}
                        >
                            <MenuItem
                                onClick={() => handleModeChange('fast')}
                                borderRadius="10px"
                                bg={localResponseMode === 'fast' ? menuItemBgActive : 'transparent'}
                                _hover={{ bg: localResponseMode === 'fast' ? menuItemHoverBg : menuItemHoverBgAlt }}
                                py={isDesktop ? 2.5 : 2}
                                px={3}
                                mb={1}
                            >
                                <Flex align="center" gap={3} w="100%">
                                    <Flex
                                        align="center"
                                        justify="center"
                                        w={isDesktop ? "30px" : "26px"}
                                        h={isDesktop ? "30px" : "26px"}
                                        borderRadius="8px"
                                        bg={localResponseMode === 'fast' ? primaryColor : menuItemActiveBgAlt}
                                        flexShrink={0}
                                    >
                                        <Box
                                            as={FaBolt}
                                            color={localResponseMode === 'fast' ? 'white' : primaryColor}
                                            fontSize={isDesktop ? "sm" : "xs"}
                                        />
                                    </Flex>
                                    <VStack align="start" spacing={0} flex={1}>
                                        <Text fontWeight="600" fontSize={isDesktop ? "sm" : "xs"} color={menuItemText}>
                                            Fast Responses
                                        </Text>
                                        <Text fontSize={isDesktop ? "xs" : "2xs"} color={menuItemDesc} lineHeight="1.3">
                                            Low latency answers
                                        </Text>
                                    </VStack>
                                </Flex>
                            </MenuItem>
                            <MenuItem
                                onClick={() => handleModeChange('expert')}
                                borderRadius="10px"
                                bg={localResponseMode === 'expert' ? menuItemBgActive : 'transparent'}
                                _hover={{ bg: localResponseMode === 'expert' ? menuItemHoverBg : menuItemHoverBgAlt }}
                                py={isDesktop ? 2.5 : 2}
                                px={3}
                            >
                                <Flex align="center" gap={3} w="100%">
                                    <Flex
                                        align="center"
                                        justify="center"
                                        w={isDesktop ? "30px" : "26px"}
                                        h={isDesktop ? "30px" : "26px"}
                                        borderRadius="8px"
                                        bg={localResponseMode === 'expert' ? primaryColor : menuItemActiveBgAlt}
                                        flexShrink={0}
                                    >
                                        <Box
                                            as={FaBrain}
                                            color={localResponseMode === 'expert' ? 'white' : primaryColor}
                                            fontSize={isDesktop ? "sm" : "xs"}
                                        />
                                    </Flex>
                                    <VStack align="start" spacing={0} flex={1}>
                                        <Text fontWeight="600" fontSize={isDesktop ? "sm" : "xs"} color={menuItemText}>
                                            Expert Reasoning
                                        </Text>
                                        <Text fontSize={isDesktop ? "xs" : "2xs"} color={menuItemDesc} lineHeight="1.3">
                                            Deeper analysis
                                        </Text>
                                    </VStack>
                                </Flex>
                            </MenuItem>
                        </MenuList>
                    </Menu>
                </Box>
                {/* Paperclip attachment button */}
                {enableFileUpload && (
                    <IconButton
                        aria-label="Attach file"
                        icon={<FaPaperclip />}
                        size="sm"
                        variant="ghost"
                        color={paperclipColor}
                        borderRadius="full"
                        position="absolute"
                        left={paperclipLeft}
                        top="50%"
                        transform="translateY(-50%)"
                        zIndex={2}
                        transition="left 0.2s ease"
                        _hover={{ bg: paperclipHoverBg, color: '#17404e' }}
                        onClick={() => fileInputRef.current?.click()}
                        isDisabled={waitAnswer || dialogueFiles.length >= MAX_FILES}
                    />
                )}
                {showNextMessage && !waitAnswer && (
                    <Tooltip label="Use the suggested message" placement="top" hasArrow borderRadius="8px" fontSize="xs">
                        <Button
                            position="absolute" right={isDesktop ? "4.4rem" : "3.9rem"} top="50%" transform="translateY(-50%)" zIndex={2}
                            size="xs" h="22px" px={2} variant="outline" borderRadius="6px"
                            fontSize="11px" fontWeight={600} color={nextHintColor} borderColor={nextHintBorder}
                            _hover={{ bg: nextHintHoverBg }}
                            onClick={acceptNextMessage} aria-label="Use the suggested message"
                        >
                            {isDesktop ? "Tab" : "Use"}
                        </Button>
                    </Tooltip>
                )}
                <InputRightElement width={isDesktop ? '4rem' : '3.5rem'} height="100%" justifyContent="flex-end" paddingRight="0.75rem">
                    <Tooltip
                        label={isOverLimit
                            ? `Context full — start a new chat`
                            : (!chatMessages || chatMessages.length === 0)
                                ? `Send message`
                                : `${Math.round(contextUsage.percentage)}% context used`
                        }
                        placement="top"
                        hasArrow
                        borderRadius="8px"
                        fontSize="xs"
                    >
                        <Box position="relative" display="inline-flex" alignItems="center" justifyContent="center">
                            <svg
                                width={isDesktop ? "50" : "44"}
                                height={isDesktop ? "50" : "44"}
                                style={{ position: 'absolute', transform: 'rotate(-90deg)', pointerEvents: 'none' }}
                            >
                                {/* Background track */}
                                <circle
                                    cx={isDesktop ? "25" : "22"}
                                    cy={isDesktop ? "25" : "22"}
                                    r={isDesktop ? "22" : "19"}
                                    fill="none"
                                    stroke={ringTrackColor}
                                    strokeWidth="3"
                                />
                                {/* Progress arc */}
                                <circle
                                    cx={isDesktop ? "25" : "22"}
                                    cy={isDesktop ? "25" : "22"}
                                    r={isDesktop ? "22" : "19"}
                                    fill="none"
                                    stroke={getRingColor(contextUsage.percentage)}
                                    strokeWidth="3"
                                    strokeLinecap="round"
                                    strokeDasharray={`${2 * Math.PI * (isDesktop ? 22 : 19)}`}
                                    strokeDashoffset={`${2 * Math.PI * (isDesktop ? 22 : 19) * (1 - contextUsage.percentage / 100)}`}
                                    style={{ transition: 'stroke-dashoffset 0.6s ease, stroke 0.4s ease' }}
                                />
                            </svg>
                            {chatStatus && chatStatus !== 'completed' ? (
                                <IconButton
                                    aria-label="Stop"
                                    size={isDesktop ? "md" : "sm"}
                                    colorScheme={dialogueBox.length === 0 ? 'gray' : "blackAlpha"}
                                    borderRadius="25px"
                                    icon={<FaStop color={dialogueBox.length === 0 ? stopIconColor : sendIconColor} />}
                                    onClick={handleStopMessage}
                                />
                            ) : (
                                <IconButton
                                    aria-label="Send Message"
                                    size={isDesktop ? "md" : "sm"}
                                    isLoading={waitAnswer}
                                    isDisabled={isOverLimit}
                                    borderRadius="25px"
                                    bg={isOverLimit ? sendButtonBgDisabled : (dialogueBox.length === 0 ? sendButtonBgDisabled : sendButtonBg)}
                                    icon={<FaPaperPlane color={isOverLimit ? sendIconColorDisabled : (dialogueBox.length === 0 ? sendIconColorDisabled : "white")} />}
                                    onClick={() => sendMessage()}
                                    id={`ChatSOC - Chat Submission ${Math.floor(chatMessages.length / 2) + 1}`}
                                />
                            )}
                        </Box>
                    </Tooltip>
                </InputRightElement>
            </InputGroup>
        </Box>
    );
});

export default DialogueInput;
